import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";

import type { PartImagePreferences } from "~/database/types";

import { useApiClient } from "../use-api-client";

export function usePartImagePreferences() {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: ["partImagePreferences"],
        queryFn: async () => {
            const response = await apiClient.get(
                "/user-preferences/part-images",
            );
            if (!response.ok) {
                throw new Error("Failed to fetch part image preferences");
            }
            const data = await response.json();
            return data.preferences as PartImagePreferences;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

export function useUpdatePartImagePreferences() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (preferences: PartImagePreferences) => {
            const response = await apiClient.put(
                "/user-preferences/part-images",
                {
                    ...preferences,
                },
            );
            if (!response.ok) {
                throw new Error("Failed to update part image preferences");
            }
            const data = await response.json();
            return data.preferences as PartImagePreferences;
        },
        onSuccess: (data) => {
            // Update the cache with the returned preferences
            queryClient.setQueryData(["partImagePreferences"], data);
            // Also invalidate to ensure fresh data
            queryClient.invalidateQueries({
                queryKey: ["partImagePreferences"],
            });
        },
    });
}
